/**
 * PeakAmbitionCollabEditor — three CollaborativeProseEditors for the
 * per-subfield co-editable fields of the team Peak Ambition block.
 *
 * Editable subfields: worldImpact, businessOutcome, pressRelease.
 * Non-editable (displayed read-only): headline, date — those have no
 * clean team field-save path today; the existing Vo-driven TeamDraftEditForm
 * remains the only write path for them (deferred to a later task).
 *
 * Room names follow the shared pattern:
 *   rb-block:<sessionId>:peakAmbition:<subfield>
 * which the backend rb-collab endpoint already accepts (validate_collab_target).
 *
 * Synthesis-load (replaceContent) for these subfield editors is OUT OF SCOPE
 * in this task — noted as follow-up for E1.
 */

import {
  CollaborativeProseEditor,
} from "@vojomo-core/shared/src/components/collab/CollaborativeProseEditor";
import {
  buildRbBlockRoomName,
  proseEditableFromRole,
} from "@vojomo-core/shared/src/components/collab/roomName";
import type { PeakAmbition } from "../types";

export interface PeakAmbitionCollabEditorProps {
  sessionId: string;
  /** Effective viewer role (owner|advisor|team_member|solo_contributor|comment|view). */
  role: string;
  /** When the block is locked, edit access is withdrawn (comment may remain). */
  isLocked: boolean;
  /** The team consensus PeakAmbition block (buildingBlocks.peakAmbition). */
  peakAmbition: PeakAmbition;
}

const COLLAB_SUBFIELDS: { key: keyof Pick<PeakAmbition, "worldImpact" | "businessOutcome" | "pressRelease">; label: string }[] = [
  { key: "worldImpact", label: "World Impact" },
  { key: "businessOutcome", label: "Business Outcome" },
  { key: "pressRelease", label: "Press Release" },
];

export function PeakAmbitionCollabEditor({
  sessionId,
  role,
  isLocked,
  peakAmbition,
}: PeakAmbitionCollabEditorProps) {
  const { canEdit, canComment } = proseEditableFromRole(role, isLocked);

  return (
    <div className="space-y-4">
      {COLLAB_SUBFIELDS.map(({ key, label }) => {
        const roomName = buildRbBlockRoomName(sessionId, "peakAmbition", key);
        const fallbackContent = peakAmbition[key] ?? "";

        return (
          <div key={key}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              {label}
            </p>
            {/* No fixed-height wrapper: the editor self-sizes (short by
                default, grows with text, caps + scrolls at its own max-h). */}
            <CollaborativeProseEditor
              roomName={roomName}
              editable={canEdit}
              canComment={canComment}
              canResolve={canComment}
              canSuggest={canComment}
              canAcceptReject={canEdit}
              fallbackContent={fallbackContent}
              placeholder={`Write the team ${label.toLowerCase()} together…`}
            />
          </div>
        );
      })}

      {/* headline + date: read-only display. No team field-save path exists
          today — the Vo-driven TeamDraftEditForm remains the only write path.
          These are deferred to a future task. */}
      {(peakAmbition.headline || peakAmbition.date) && (
        <div className="space-y-2 border-t border-slate-100 pt-3 mt-1">
          {peakAmbition.headline && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">
                Headline
              </p>
              <p className="text-xs text-foreground/70 leading-relaxed">{peakAmbition.headline}</p>
            </div>
          )}
          {peakAmbition.date && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">
                Target Date
              </p>
              <p className="text-xs text-foreground/70">{peakAmbition.date}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
